import { useMemo } from 'react';

import { centsToDollar } from '@givebacks/utils/currency';
import { Box, Card, Collapse, Divider, Group, Stack, UnstyledButton } from '@mantine/core';
import { DateTime } from 'luxon';

import { Badge } from '../../../../DataDisplay';
import { Text } from '../../../../Typography';
import { QuantitySelector } from '../../buttons';
import { CalendarDate } from '../CalendarDate';
import classes from './TicketCard.module.css';

interface TicketCardProps {
  title: string;
  price: number;
  description?: string;
  inventoryAvailable?: number | false;
  eventDate?: string;
  sellDate?: string;

  /**
   * If true, will show the quantity selector
   */
  opened?: boolean;
  onQuantityChange?: (quantity: number) => void;
  quantity?: number;
  onClick?: () => void;
}

const TicketCard = ({
  title,
  price,
  description,
  eventDate,
  sellDate,
  opened = false,
  onQuantityChange,
  quantity = 0,
  inventoryAvailable,
  onClick,
}: TicketCardProps) => {
  const ticketCardProperties = useMemo(() => {
    if (inventoryAvailable === 0) {
      return { isDisabled: true, isSoldOut: true };
    }

    const sellingDate = DateTime.fromISO(sellDate);

    const currentDate = DateTime.now();
    const daysUntilSellDate = Math.abs(Math.ceil(sellingDate.diff(currentDate, 'days').days));
    return { isDisabled: currentDate < sellingDate, daysUntilSellDate };
  }, [inventoryAvailable, sellDate]);

  const { isDisabled, daysUntilSellDate, isSoldOut } = ticketCardProperties;

  return (
    <UnstyledButton w="100%">
      <Card
        className={classes.TicketCard}
        mod={{
          disabled: isDisabled,
          opened,
        }}
        onClick={!isDisabled ? onClick : undefined}
      >
        <Group wrap="nowrap">
          {!!eventDate && <CalendarDate date={eventDate ?? ''} />}

          <Box flex={1}>
            <Text fw="600" c="gray.8" fz="14" lineClamp={1}>
              {title}
            </Text>

            <Group wrap="nowrap" justify="space-between" pt={4}>
              <Text fw="400" c="gray.8" fz="14" lineClamp={1}>
                {centsToDollar(price)}{' '}
                <Text span fw="400" c="gray.4" fz="14">
                  each
                </Text>
              </Text>

              {!isDisabled && (
                <Group wrap="nowrap" gap="xs">
                  {inventoryAvailable === false ? null : (
                    <Badge type="warning" variant="light" miw={64}>
                      {inventoryAvailable ?? 0} Left
                    </Badge>
                  )}
                  {quantity > 0 && (
                    <Badge type="primary" variant="light">
                      {quantity} Selected
                    </Badge>
                  )}
                </Group>
              )}

              {isDisabled && (
                <>
                  {daysUntilSellDate != null && (
                    <Badge type="neutral" variant="light">
                      Sale starts in {daysUntilSellDate} days
                    </Badge>
                  )}

                  {isSoldOut === true && (
                    <Badge type="error" variant="light">
                      Sold Out
                    </Badge>
                  )}
                </>
              )}
            </Group>
          </Box>
        </Group>

        <Collapse in={opened}>
          <Stack mt="md">
            <Divider size="xs" />

            {description && (
              <Text c="gray.8" fz="10" fw="400">
                {description}
              </Text>
            )}

            <QuantitySelector
              minValue={1}
              size={'lg'}
              value={quantity}
              maxValue={inventoryAvailable === false ? undefined : inventoryAvailable}
              onChange={(newValue) => onQuantityChange?.(newValue)}
              label="Select ticket quantity"
            />
          </Stack>
        </Collapse>
      </Card>
    </UnstyledButton>
  );
};

export { TicketCard };
export type { TicketCardProps };
